import React, { useState, useEffect } from 'react'
import FinancialClassForm from '../components/FinancialClassForm'
import { apiService } from '../services/apiService'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  FolderIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const FinancialClassesPage = () => {
  const [financialClasses, setFinancialClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingClass, setEditingClass] = useState(null)

  useEffect(() => {
    loadFinancialClasses()
  }, [])

  const loadFinancialClasses = async () => {
    try {
      setLoading(true)
      const result = await apiService.getFinancialClasses()
      setFinancialClasses(result.data || [])
    } catch (error) {
      console.error('Erro ao carregar classes financeiras:', error)
      setFinancialClasses([])
      toast.error('Erro ao carregar classes financeiras')
    } finally {
      setLoading(false)
    }
  }

  const handleView = (financialClass) => {
    toast(`Visualizando classe: ${financialClass.name}`, {
      icon: 'ℹ️',
    })
  }

  const handleEdit = (financialClass) => {
    setEditingClass(financialClass)
    setShowForm(true)
  }

  const handleDelete = async (id, name) => {
    if (!confirm(`Tem certeza que deseja desativar a classe financeira "${name}"?`)) {
      return
    }

    try {
      await apiService.deleteFinancialClass(id)
      toast.success('Classe financeira desativada com sucesso!')
      loadFinancialClasses()
    } catch (error) {
      console.error('Erro ao desativar classe financeira:', error)
      toast.error('Erro ao desativar classe financeira')
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingClass(null)
  }

  const handleFormSuccess = () => {
    setShowForm(false)
    setEditingClass(null)
    loadFinancialClasses()
  }

  const handleSeed = async () => {
    if (!confirm('Deseja popular com a estrutura padrão de DRE?\n\nIsso criará 20 classes financeiras organizadas hierarquicamente.')) {
      return
    }

    try {
      const result = await apiService.seedFinancialClasses()
      toast.success(`Estrutura padrão criada! ${result.created} classes criadas, ${result.skipped} já existiam.`)
      loadFinancialClasses()
    } catch (error) {
      console.error('Erro ao popular classes:', error)
      toast.error('Erro ao popular classes financeiras')
    }
  }

  const filteredClasses = financialClasses.filter(item => {
    const searchLower = searchQuery.toLowerCase()
    return (
      item.name.toLowerCase().includes(searchLower) ||
      item.code.toLowerCase().includes(searchLower) ||
      item.type.toLowerCase().includes(searchLower)
    )
  })

  const getTypeLabel = (type) => {
    const types = {
      'receita': 'Receita',
      'despesa': 'Despesa',
      'ativo': 'Ativo',
      'passivo': 'Passivo'
    }
    return types[type] || type
  }

  const getTypeColor = (type) => {
    const colors = {
      'receita': 'bg-green-100 text-green-800',
      'despesa': 'bg-red-100 text-red-800',
      'ativo': 'bg-blue-100 text-blue-800',
      'passivo': 'bg-yellow-100 text-yellow-800'
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Carregando classes financeiras...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <ChartBarIcon className="h-8 w-8 text-blue-600 mr-3" />
            Classes Financeiras / DRE
          </h1>
          <p className="text-gray-600 mt-1">
            Gerencie as classes do plano de contas e DRE
          </p>
        </div>

        <div className="flex gap-3">
          {financialClasses.length === 0 && (
            <button
              onClick={handleSeed}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <FolderIcon className="h-5 w-5" />
              <span>Popular Estrutura Padrão</span>
            </button>
          )}
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Nova Classe</span>
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome, código ou tipo..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {filteredClasses.length === 0 ? (
          <div className="text-center py-12">
            <FolderIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'Nenhuma classe encontrada' : 'Backend implementado'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchQuery
                ? 'Tente ajustar os filtros de busca.'
                : 'API funcionando. Frontend completo em desenvolvimento.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Classe Pai
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredClasses.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {item.code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(item.type)}`}>
                        {getTypeLabel(item.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.parent_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(item)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Visualizar"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                          title="Editar"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id, item.name)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Desativar"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Formulário Modal */}
      {showForm && (
        <FinancialClassForm
          financialClass={editingClass}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  )
}

export default FinancialClassesPage